import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CleanerCard from "@/components/CleanerCard";
import { SlidersHorizontal, Loader2, Search as SearchIcon } from "lucide-react";
import DemandCapture from "@/components/DemandCapture";
import LocationAutocomplete from "@/components/LocationAutocomplete";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const [services, setServices] = useState([]);
  const [cleaners, setCleaners] = useState([]);
  const [loading, setLoading] = useState(false);
  // Results (and the no-results / Help Us Help You state) appear only after a deliberate search.
  const [searched, setSearched] = useState(false);
  const [criteria, setCriteria] = useState(null);
  const [place, setPlace] = useState(params.get('location') ? { place_id: params.get('place_id') || '', label: params.get('location') } : null);

  const [filters, setFilters] = useState({
    service: params.get("service") || "",
    place_id: params.get("place_id") || "",
    location: params.get("location") || params.get("postcode") || "",
    q: params.get("q") || "",
    max_rate: "",
    min_rating: "",
    date: params.get("date") || "",
    slot: params.get("slot") || "",
    dbs_verified: false,
    identity_verified: false,
    insured: false,
    company_verified: false,
    sort: "recommended",
  });

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data.services)).catch(() => {});
  }, []);

  const runSearch = useCallback(async (f) => {
    setLoading(true);
    setSearched(true);
    setCriteria(f);
    const clean = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "" && v != null && v !== false));
    try {
      const { data } = await api.get("/cleaners", { params: clean });
      setCleaners(data.cleaners);
      // Silently record unmet demand (no email) so admin reporting shows where cover is missing.
      if (data.cleaners.length === 0 && (clean.location || clean.place_id || clean.service)) {
        api.post("/demand", {
          postcode: f.location, service: f.service, date: f.date,
          slot: f.slot, context: "search", notify_me: false, results_count: 0,
        }).catch(() => {});
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // A search started from the homepage/dashboard already carries criteria — honour it once.
  useEffect(() => {
    if (params.get("postcode") || params.get("location") || params.get("place_id") || params.get("service") || params.get("date") || params.get("q")) {
      runSearch({
        service: params.get("service") || "",
        place_id: params.get("place_id") || "", location: params.get("location") || params.get("postcode") || "",
        q: params.get("q") || "", max_rate: "", min_rating: "",
        date: params.get("date") || "", slot: params.get("slot") || "",
        dbs_verified: false, identity_verified: false, insured: false,
        company_verified: false, sort: "recommended",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e) => {
    e.preventDefault();
    runSearch(filters);
  };

  const upd = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight">Find your cleaner</h1>
        {searched && !loading ? (
          <p className="text-muted-foreground mt-2" data-testid="results-count">
            {cleaners.length === 0
              ? "No matches for this search"
              : `${cleaners.length} cleaner${cleaners.length === 1 ? "" : "s"} available`}
          </p>
        ) : (
          <p className="text-muted-foreground mt-2" data-testid="search-prompt">
            Tell us what you need and press Search.
          </p>
        )}

        <div className="grid lg:grid-cols-[280px_1fr] gap-8 mt-8">
          {/* FILTERS */}
          <form onSubmit={submit} className="bg-card rounded-2xl border border-primary/5 p-6 h-fit lg:sticky lg:top-24" data-testid="filters-panel">
            <div className="flex items-center gap-2 mb-5 font-display font-semibold">
              <SlidersHorizontal className="h-4 w-4" /> Filters
            </div>

            <label className="text-sm font-medium">Service</label>
            <select value={filters.service} onChange={(e) => upd("service", e.target.value)} className="w-full mt-1 mb-4 px-3 py-2.5 rounded-xl border border-border bg-background outline-none focus:border-accent" data-testid="filter-service">
              <option value="">All services</option>
              {services.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
            </select>

            <label className="text-sm font-medium">Location <span className="text-muted-foreground font-normal">(optional)</span></label>
            <div className="mt-1 mb-1">
              <LocationAutocomplete testid="filter-location" value={place}
                onSelect={(p2) => { setPlace(p2); setFilters((f) => ({ ...f, place_id: p2.place_id, location: p2.label })); }}
                onClear={() => { setPlace(null); setFilters((f) => ({ ...f, place_id: "", location: "" })); }}
                placeholder="Any location — start typing a town" />
            </div>
            <p className="text-xs text-muted-foreground mb-4">Leave empty to browse cleaners anywhere.</p>

            <label className="text-sm font-medium">Available on <span className="text-muted-foreground font-normal">(optional)</span></label>
            <input type="date" min={new Date().toISOString().split("T")[0]} value={filters.date} onChange={(e) => upd("date", e.target.value)} className="w-full mt-1 px-3 py-2.5 rounded-xl border border-border bg-background outline-none focus:border-accent" data-testid="filter-date" />
            <div className="flex items-center justify-between mt-1 mb-4">
              <span className="text-xs text-muted-foreground" data-testid="date-any-label">{filters.date ? "Specific date selected" : "Any date"}</span>
              {filters.date && <button type="button" onClick={() => { upd("date", ""); upd("slot", ""); }} className="text-xs font-medium text-accent hover:underline" data-testid="clear-date-btn">Use any date</button>}
            </div>

            <label className="text-sm font-medium">Time of day</label>
            <select value={filters.slot} onChange={(e) => upd("slot", e.target.value)} className="w-full mt-1 mb-4 px-3 py-2.5 rounded-xl border border-border bg-background outline-none focus:border-accent" data-testid="filter-slot">
              <option value="">Any time</option>
              <option value="morning">Morning (8am–12pm)</option>
              <option value="afternoon">Afternoon (12pm–5pm)</option>
              <option value="evening">Evening (5pm–9pm)</option>
            </select>

            <label className="text-sm font-medium">Max rate (£/hr)</label>
            <input type="number" value={filters.max_rate} onChange={(e) => upd("max_rate", e.target.value)} placeholder="Any" className="w-full mt-1 mb-4 px-3 py-2.5 rounded-xl border border-border bg-background outline-none focus:border-accent" data-testid="filter-rate" />

            <label className="text-sm font-medium">Minimum rating</label>
            <select value={filters.min_rating} onChange={(e) => upd("min_rating", e.target.value)} className="w-full mt-1 mb-4 px-3 py-2.5 rounded-xl border border-border bg-background outline-none focus:border-accent" data-testid="filter-rating">
              <option value="">Any</option>
              <option value="4.5">4.5+</option>
              <option value="4">4.0+</option>
              <option value="3">3.0+</option>
            </select>

            <label className="text-sm font-medium">Verification</label>
            <div className="mt-1 mb-4 space-y-2" data-testid="filter-verification">
              {[
                { key: "dbs_verified", label: "DBS Verified" },
                { key: "identity_verified", label: "Identity Verified" },
                { key: "insured", label: "Insured" },
                { key: "company_verified", label: "Cleaning Company Verified" },
              ].map((f) => (
                <label key={f.key} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!filters[f.key]} onChange={(e) => upd(f.key, e.target.checked)} data-testid={`filter-${f.key.replace(/_/g, "-")}`} />
                  {f.label}
                </label>
              ))}
            </div>

            <label className="text-sm font-medium">Sort by</label>
            <select value={filters.sort} onChange={(e) => upd("sort", e.target.value)} className="w-full mt-1 px-3 py-2.5 rounded-xl border border-border bg-background outline-none focus:border-accent" data-testid="filter-sort">
              <option value="recommended">Recommended</option>
              <option value="rating">Highest rated</option>
              <option value="price_low">Price: low to high</option>
              <option value="price_high">Price: high to low</option>
              <option value="experience">Most experienced</option>
            </select>

            <button type="submit" disabled={loading} className="w-full mt-5 px-6 py-3 rounded-full bg-accent text-white font-medium hover:bg-accent/90 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="search-submit-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <SearchIcon className="h-4 w-4" />} Search
            </button>
          </form>

          {/* RESULTS */}
          <div>
            {loading ? (
              <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-accent" /></div>
            ) : !searched ? (
              <div className="text-center py-20 bg-card rounded-2xl border border-primary/5" data-testid="search-idle">
                <SearchIcon className="h-12 w-12 mx-auto text-muted-foreground/40" />
                <p className="mt-4 font-display text-lg font-semibold">Ready when you are</p>
                <p className="text-muted-foreground mt-1 max-w-sm mx-auto">
                  Press Search to browse every cleaner on My Clean Connect™, or narrow it down first
                  by service, location, date or time — every filter is optional.
                </p>
                <button type="button" onClick={() => runSearch(filters)} className="mt-5 px-6 py-2.5 rounded-full bg-accent text-white text-sm font-medium hover:bg-accent/90 transition-colors" data-testid="browse-all-btn">
                  Browse all cleaners
                </button>
              </div>
            ) : cleaners.length === 0 ? (
              <div className="text-center py-16 bg-card rounded-2xl border border-primary/5" data-testid="no-results">
                <SearchIcon className="h-12 w-12 mx-auto text-muted-foreground/40" />
                <p className="mt-4 font-display text-lg font-semibold">
                  Ahh, unfortunately we don't have a cleaner matching your search just yet.
                </p>
                <p className="text-muted-foreground mt-1 max-w-sm mx-auto">
                  We're recruiting in new areas every week — tell us what you need and we'll get
                  cover to you.
                </p>
                <div className="text-left max-w-xl mx-auto"><DemandCapture filters={{ ...(criteria || filters), postcode: (criteria || filters).location }} /></div>
                <button onClick={() => { setFilters({ service: "", place_id: "", location: "", q: "", max_rate: "", min_rating: "", date: "", slot: "", dbs_verified: false, identity_verified: false, insured: false, company_verified: false, sort: "recommended" }); setPlace(null); setSearched(false); setCleaners([]); }} className="mt-5 text-sm font-medium text-accent hover:underline" data-testid="clear-filters-btn">Start a new search</button>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="cleaner-results">
                {cleaners.map((c) => <CleanerCard key={c.cleaner_id} c={c} bookContext={{ service: filters.service, date: filters.date, slot: filters.slot, property: params.get("property") || "" }} />)}
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
